import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Plus, X } from 'lucide-react';
import { Button } from '../ui/button';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';

const COLOR_OPTIONS = [
  { value: 'indigo',  hex: '#6366f1' },
  { value: 'violet',  hex: '#a855f7' },
  { value: 'pink',    hex: '#ec4899' },
  { value: 'emerald', hex: '#10b981' },
  { value: 'amber',   hex: '#f59e0b' },
];

const COLOR_MAP = COLOR_OPTIONS.reduce((acc, c) => ({ ...acc, [c.value]: c.hex }), {});

const CalendarWidget = () => {
  const { user, refreshTick } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ title: '', date: '', time: '', color: 'indigo' });
  const [saving, setSaving] = useState(false);

  const fetchEvents = async () => {
    try {
      const { data, error } = await supabase
        .from('calendar_events')
        .select('*')
        .eq('user_id', user.id);
      if (error) throw error;
      setEvents(data || []);
    } catch (err) {
      console.error('CalendarWidget fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    fetchEvents();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, refreshTick]);

  const prevMonth = () =>
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  const nextMonth = () =>
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return {
      daysInMonth: new Date(year, month + 1, 0).getDate(),
      startingDayOfWeek: new Date(year, month, 1).getDay(),
    };
  };

  const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentDate);
  const monthName = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const getEventsForDay = (day) =>
    events.filter((event) => {
      const d = new Date(event.date + 'T00:00:00');
      return (
        d.getDate() === day &&
        d.getMonth() === currentDate.getMonth() &&
        d.getFullYear() === currentDate.getFullYear()
      );
    });

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const upcoming = events
    .filter((e) => new Date(e.date + 'T00:00:00') >= today)
    .slice(0, 3);

  const handleCreate = async () => {
    if (!form.title.trim() || !form.date) return;
    setSaving(true);
    try {
      const { error } = await supabase.from('calendar_events').insert({
        user_id: user.id,
        title: form.title.trim(),
        date: form.date,
        time: form.time || null,
        color: form.color,
      });
      if (error) throw error;
      setShowModal(false);
      setForm({ title: '', date: '', time: '', color: 'indigo' });
      await fetchEvents();
    } catch (err) {
      console.error('CalendarWidget create error:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Card className="bg-white/75 backdrop-blur-md border border-white/90 shadow-[0_4px_20px_rgba(99,102,241,0.06)] dark:bg-[#111114] dark:border-[#1e1e24] dark:shadow-none hover:shadow-[0_8px_30px_rgba(99,102,241,0.1)] dark:hover:shadow-none transition-all duration-300 overflow-hidden">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4 pb-4 border-b border-indigo-50/50 dark:border-[#1a1a20]">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-[rgba(99,102,241,0.08)] dark:bg-[rgba(167,139,250,0.1)] rounded-xl">
                <CalendarIcon className="text-indigo-500 dark:text-violet-400" size={24} />
              </div>
              <h2 className="text-xl font-semibold text-[#1a1625] dark:text-[#e5e5e5]">Calendar</h2>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-indigo-300 hover:text-indigo-500 dark:text-[#555] dark:hover:text-violet-400"
              onClick={() => setShowModal(true)}
            >
              <Plus size={16} />
            </Button>
          </div>

          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-[#6b7280] hover:text-[#1a1625] dark:text-[#888] dark:hover:text-[#e5e5e5]"
              onClick={prevMonth}
            >
              <ChevronLeft size={16} />
            </Button>
            <span className="text-sm font-medium text-[#1a1625] dark:text-[#e5e5e5]">{monthName}</span>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-[#6b7280] hover:text-[#1a1625] dark:text-[#888] dark:hover:text-[#e5e5e5]"
              onClick={nextMonth}
            >
              <ChevronRight size={16} />
            </Button>
          </div>

          {/* Calendar Grid */}
          <div className="mb-4">
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                <div
                  key={index}
                  className="text-center text-xs font-medium text-[#9ca3af] dark:text-[#555] py-1"
                >
                  {day}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: startingDayOfWeek }).map((_, index) => (
                <div key={`empty-${index}`} className="aspect-square" />
              ))}
              {Array.from({ length: daysInMonth }).map((_, index) => {
                const day = index + 1;
                const dayEvents = getEventsForDay(day);
                const isToday =
                  day === new Date().getDate() &&
                  currentDate.getMonth() === new Date().getMonth() &&
                  currentDate.getFullYear() === new Date().getFullYear();

                return (
                  <div
                    key={day}
                    className={`aspect-square flex items-center justify-center text-sm rounded-lg cursor-pointer transition-all duration-200 ${
                      isToday
                        ? 'bg-gradient-to-br from-indigo-500 to-purple-500 text-white font-bold shadow-md'
                        : dayEvents.length > 0
                        ? 'bg-indigo-100 dark:bg-[rgba(99,102,241,0.15)] text-indigo-700 dark:text-violet-300 font-medium'
                        : 'text-[#6b7280] dark:text-[#888] hover:bg-indigo-50/50 dark:hover:bg-[#1a1a20]'
                    }`}
                  >
                    {day}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Upcoming Events */}
          <div className="space-y-2 max-h-[200px] overflow-y-auto custom-scrollbar">
            <h3 className="text-sm font-semibold text-[#1a1625] dark:text-[#e5e5e5] mb-2">Upcoming</h3>
            {loading ? (
              <div className="space-y-2">
                {[1, 2].map((i) => (
                  <div key={i} className="h-14 rounded-xl bg-indigo-50/50 dark:bg-[#16161b] animate-pulse" />
                ))}
              </div>
            ) : upcoming.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-6 text-center">
                <CalendarIcon size={28} className="text-indigo-200 dark:text-[#333] mb-2" />
                <p className="text-sm text-[#6b7280] dark:text-[#888]">No upcoming events</p>
                <p className="text-xs text-[#9ca3af] dark:text-[#555]">Tap + to add one</p>
              </div>
            ) : (
              upcoming.map((event) => (
                <div
                  key={event.id}
                  className="p-3 bg-gradient-to-br from-indigo-50/50 to-white dark:from-[#16161b] dark:to-[#111114] rounded-xl border border-indigo-50/80 dark:border-[#1a1a20]"
                >
                  <div className="flex items-start space-x-3">
                    <div
                      className="w-1 self-stretch rounded-full flex-shrink-0"
                      style={{ backgroundColor: COLOR_MAP[event.color] ?? event.color }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#1a1625] dark:text-[#e5e5e5]">{event.title}</p>
                      <p className="text-xs text-[#6b7280] dark:text-[#888]">
                        {new Date(event.date + 'T00:00:00').toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                        })}
                        {event.time && ` at ${event.time}`}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </Card>

      {/* Create Event Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white dark:bg-[#111114] rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4 border border-white/90 dark:border-[#1e1e24]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#1a1625] dark:text-[#e5e5e5]">New Event</h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-[#9ca3af] hover:text-[#6b7280] dark:text-[#555] dark:hover:text-[#888] transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-[#6b7280] dark:text-[#888] mb-1 block">Title *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Event title"
                  className="w-full px-3 py-2 rounded-lg border border-indigo-100 dark:border-[#1e1e24] bg-white dark:bg-[#1a1a20] text-sm text-[#1a1625] dark:text-[#e5e5e5] focus:outline-none focus:ring-2 focus:ring-indigo-400 dark:focus:ring-violet-500 placeholder-[#9ca3af] dark:placeholder-[#555]"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[#6b7280] dark:text-[#888] mb-1 block">Date *</label>
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-indigo-100 dark:border-[#1e1e24] bg-white dark:bg-[#1a1a20] text-sm text-[#1a1625] dark:text-[#e5e5e5] focus:outline-none focus:ring-2 focus:ring-indigo-400 dark:focus:ring-violet-500"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[#6b7280] dark:text-[#888] mb-1 block">Time</label>
                <input
                  type="time"
                  value={form.time}
                  onChange={(e) => setForm({ ...form, time: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-indigo-100 dark:border-[#1e1e24] bg-white dark:bg-[#1a1a20] text-sm text-[#1a1625] dark:text-[#e5e5e5] focus:outline-none focus:ring-2 focus:ring-indigo-400 dark:focus:ring-violet-500"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[#6b7280] dark:text-[#888] mb-2 block">Color</label>
                <div className="flex space-x-2">
                  {COLOR_OPTIONS.map((c) => (
                    <button
                      key={c.value}
                      onClick={() => setForm({ ...form, color: c.value })}
                      className={`w-7 h-7 rounded-full transition-all ${
                        form.color === c.value ? 'ring-2 ring-offset-2 ring-indigo-400 scale-110' : ''
                      }`}
                      style={{ backgroundColor: c.hex }}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="flex space-x-2 mt-5">
              <Button
                variant="outline"
                className="flex-1 border-indigo-100 dark:border-[#1e1e24]"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white border-0"
                onClick={handleCreate}
                disabled={saving || !form.title.trim() || !form.date}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CalendarWidget;
